window.AI_PET_GENERATOR = window.AI_PET_GENERATOR || {};

window.AI_PET_GENERATOR.init = function initAIPetGenerator(root = document) {
  const section = root.querySelector('[data-section-id="ai-pet-generator-block"]');
  if (!section) return;

  if (section.__aiController && typeof section.__aiController.abort === 'function') {
    try { section.__aiController.abort(); } catch (_) {}
  }
  const controller = new AbortController();
  const { signal } = controller;
  section.__aiController = controller;

  section.dataset.aiBound = '1';
  const on = (el, type, handler, opts = {}) => {
    if (!el) return;
    el.addEventListener(type, handler, { ...opts, signal });
  };

  const rafThrottle = (fn) => {
    let scheduled = false;
    return (...args) => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        fn(...args);
      });
    };
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const withRetries = async (fn, { interval = 250, timeout = 8000 } = {}) => {
    const start = performance.now();
    while (true) {
      const res = fn();
      if (res) return res;
      if (performance.now() - start > timeout) return null;
      await sleep(interval);
    }
  };

  const esc = (s) =>
    window.CSS?.escape ? CSS.escape(String(s)) : String(s).replace(/[^a-zA-Z0-9_-]/g, '\\$&');

  const isVisible = (el) => !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));

  const PRODUCT_SCOPE =
    section.closest('product-info') ||
    section.closest('product-form') ||
    section.closest('#MainContent') ||
    section.closest('main') ||
    document;

  const getAiAnchor = () =>
    section.querySelector('#ai-pet-generator-an') || document.getElementById('ai-pet-generator-an');

  const getProductId = () => {
    const an = getAiAnchor();
    const pid = an?.getAttribute?.('product-id') || '';
    return String(pid || '').trim();
  };

  const getProductHandle = () => {
    const an = getAiAnchor();
    const ph = an?.getAttribute?.('product-handle') || '';
    return String(ph || '').trim();
  };

  const PRODUCT_ID = getProductId() || 'unknown';
  const LS_KEY = `ai-pet-generator:${PRODUCT_ID}`;

  const FILE_PROPERTY_KEY = 'AI Image';
  // const URL_PROPERTY_KEY = '_AI Image URL';

  const MAX_GENERATIONS = 2;

  /* ------------------------- upload UI (dropzone) ------------------------- */
  const input = section.querySelector('#ai-photo');
  const dropzone = section.querySelector('#ai-photo-dropzone');
  const filenameEl = section.querySelector('#ai-photo-filename');
  const ctaEl = section.querySelector('#ai-photo-cta');
  const previewEl = section.querySelector('#ai-photo-preview');

  let lastObjectUrl = null;
  const revokePreviewUrl = () => {
    if (lastObjectUrl) {
      try { URL.revokeObjectURL(lastObjectUrl); } catch (_) {}
      lastObjectUrl = null;
    }
  };

  const clearPreview = () => {
    revokePreviewUrl();
    previewEl?.removeAttribute?.('src');
  };

  const setUIFromFile = () => {
    if (!input || !dropzone || !filenameEl || !ctaEl || !previewEl) return;

    const file = input.files?.[0];
    if (!file) {
      clearPreview();
      filenameEl.textContent = '';
      ctaEl.textContent = 'Upload file';
      dropzone.classList.remove('has-file');
      return;
    }

    clearPreview();
    lastObjectUrl = URL.createObjectURL(file);
    previewEl.src = lastObjectUrl;

    filenameEl.textContent = file.name || '';
    ctaEl.textContent = 'Change file';
    dropzone.classList.add('has-file');
  };

  if (input && dropzone && filenameEl && ctaEl && previewEl) {
    on(input, 'change', setUIFromFile);

    const prevent = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    for (const ev of ['dragenter', 'dragover']) {
      on(dropzone, ev, (e) => {
        prevent(e);
        dropzone.classList.add('is-dragover');
      });
    }

    for (const ev of ['dragleave', 'drop']) {
      on(dropzone, ev, (e) => {
        prevent(e);
        dropzone.classList.remove('is-dragover');
      });
    }

    on(dropzone, 'drop', (e) => {
      const files = e.dataTransfer?.files;
      if (!files?.length) return;

      const dt = new DataTransfer();
      for (const f of Array.from(files)) dt.items.add(f);
      input.files = dt.files;

      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    signal.addEventListener('abort', revokePreviewUrl, { once: true });

    setUIFromFile();
  }

  const hasSelectedImage = () => !!section.querySelector('.ai-option.selected');

  const findAddToCartForms = () => {
    const scope = PRODUCT_SCOPE === document ? document : PRODUCT_SCOPE;
    return Array.from(scope.querySelectorAll('form[action*="/cart/add"], form[action*="/cart/add.js"]'));
  };

  const buttonsForForm = (form) => {
    const btns = [
      form.querySelector('button[name="add"]'),
      form.querySelector('button[type="submit"]'),
      form.querySelector('input[name="add"]'),
      form.querySelector('input[type="submit"]'),
    ].filter(Boolean);

    if (form.id) {
      btns.push(
        ...Array.from(document.querySelectorAll(`button[form="${esc(form.id)}"], input[form="${esc(form.id)}"]`))
      );
    }

    return btns;
  };

  const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));

  const findAddToCartButtons = () => {
    const forms = findAddToCartForms();
    let btns = forms.flatMap(buttonsForForm);

    if (!btns.length) {
      const scope = PRODUCT_SCOPE === document ? document : PRODUCT_SCOPE;
      btns = btns.concat(
        Array.from(scope.querySelectorAll('button, input[type="submit"], input[name="add"]')).filter((el) => {
          const label = `${el.getAttribute('aria-label') || ''} ${el.textContent || ''}`.trim();
          return /add to cart/i.test(label);
        })
      );
    }

    return uniq(btns);
  };

  let addButtons = [];

  const applyEnabled = (enabled) => {
    for (const btn of addButtons) {
      if (!btn?.isConnected) continue;
      try { btn.disabled = !enabled; } catch (_) {}
      btn.setAttribute('aria-disabled', String(!enabled));
      btn.classList.toggle('is-disabled-by-generator', !enabled);
    }
  };

  const bindSubmitGuards = () => {
    for (const form of findAddToCartForms()) {
      if (form.dataset.aiAtcGuardBound === '1') continue;
      form.dataset.aiAtcGuardBound = '1';

      on(
        form,
        'submit',
        (e) => {
          if (!hasSelectedImage()) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        { capture: true }
      );
    }
  };

  const scheduleRefresh = rafThrottle(() => {
    addButtons = findAddToCartButtons();
    applyEnabled(hasSelectedImage());
    bindSubmitGuards();
  });

  scheduleRefresh();
  void withRetries(
    () => {
      scheduleRefresh();
      return addButtons.length ? addButtons : null;
    },
    { interval: 250, timeout: 8000 }
  );

  on(section, 'click', (e) => {
    if (!e.target?.closest?.('.ai-option')) return;
    queueMicrotask(scheduleRefresh);
  });

  const resultsRootForGate = section.querySelector('.ai-results') || section;
  const moResultsGate = new MutationObserver(scheduleRefresh);
  moResultsGate.observe(resultsRootForGate, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });
  signal.addEventListener('abort', () => moResultsGate.disconnect(), { once: true });

  const scopeNode = PRODUCT_SCOPE === document ? document.body : PRODUCT_SCOPE;
  const moScopeGate = new MutationObserver(scheduleRefresh);
  moScopeGate.observe(scopeNode, { childList: true, subtree: true });
  signal.addEventListener('abort', () => moScopeGate.disconnect(), { once: true });

on(
  document,
  'click',
  (e) => {
    const btn = e.target?.closest?.('button[name="add"], button[type="submit"], input[name="add"], input[type="submit"]');
    if (!btn) return;

    const form = btn.closest('form[action*="/cart/add"]');
    if (!form) return;

    if (!hasSelectedImage()) {
      e.preventDefault();
      e.stopPropagation();
    }
  },
  { capture: true }
);
  const photoInput = section.querySelector('#ai-photo');
  const nameInput = section.querySelector('#ai-pet-name');
  const styleSelect = section.querySelector('#ai-style');
  const btn = section.querySelector('#ai-generate-btn');

  const status = section.querySelector('.ai-status');
  const results = section.querySelector('.ai-results');
  const grid = section.querySelector('.ai-grid');
  const loadingBox = section.querySelector('.ai-loading');
  const chooseLabel = section.querySelector('.ai-choose-label');
  const pillButtons = Array.from(section.querySelectorAll('[data-ai-style-pill]'));

  const uploadBox = photoInput ? photoInput.closest('.ai-upload') || photoInput.parentElement : null;

  if (loadingBox && !loadingBox.dataset.portalized) {
    loadingBox.dataset.portalized = '1';
    document.body.appendChild(loadingBox);
  }

  /* ------------------------------- state ------------------------------- */
  const generatedImages = [];
  let selectedImage = null;
  let generationCount = 0;

  let _selectedImageFile = null;
  let _selectedImageFileUrl = null;
  let _selectedImageFilePromise = null;

  let dogPopupTimer = null;

  /* ------------------------------- LS helpers ------------------------------- */
  const safeParse = (json) => {
    try { return JSON.parse(json); } catch { return null; }
  };

  const readLS = () => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      const data = raw ? safeParse(raw) : null;
      return data && typeof data === 'object' ? data : null;
    } catch {
      return null;
    }
  };

  const writeLS = (payload) => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(payload)); } catch {}
  };

  const saveStateToLS = () => {
    writeLS({
      generatedImages: generatedImages.slice(0, MAX_GENERATIONS),
      selectedImage: selectedImage || null,
      petName: nameInput?.value || '',
      style: styleSelect?.value || '',
      generationCount: generationCount || 0,
      updatedAt: Date.now(),
    });
  };

  const shouldRequireEmailGate = () => {
    const lsData = readLS();
    if (!lsData || typeof lsData !== 'object') return true;

    const imgs = Array.isArray(lsData.generatedImages) ? lsData.generatedImages : [];
    const hasAtLeastOneImage = imgs.some((it) => it && typeof it.url === 'string' && it.url.trim());
    return !hasAtLeastOneImage;
  };

  /* ------------------------------ status helpers ------------------------------ */
  const clearStatus = () => {
    if (!status) return;
    status.hidden = true;
    status.textContent = '';
  };

  const showError = (msg) => {
    if (!status) return;
    status.hidden = false;
    status.textContent = msg || 'Error';
  };

const showLoading = () => {
  if (!loadingBox) return;
  loadingBox.hidden = false;
  loadingBox.style.display = 'grid';
  loadingBox.style.pointerEvents = 'auto';
  loadingBox.setAttribute('aria-hidden', 'false');
};

const hideLoading = () => {
  if (!loadingBox) return;
  loadingBox.hidden = true;
  loadingBox.style.display = 'none';
  loadingBox.style.pointerEvents = 'none';
  loadingBox.setAttribute('aria-hidden', 'true');
};

  const showLimitMessage = () => {
    if (!status) return;
    status.hidden = false;
    status.textContent = 'You can only generate 2 images. Add to cart to generate more 🐾';
  };

  const disableGenerateDueToLimit = () => {
    if (btn) btn.disabled = true;
  };

  /* ------------------------------ field errors ------------------------------ */
  const ensureFieldErrorEl = (field) => {
    let el = section.querySelector(`[data-ai-error="${field}"]`);
    if (el) return el;

    el = document.createElement('small');
    el.dataset.aiError = field;
    el.className = 'ai-field-error';
    el.hidden = true;

    if (field === 'photo') {
      (uploadBox || photoInput?.parentElement || section).appendChild(el);
    } else if (field === 'name') {
      (nameInput?.parentElement || section).appendChild(el);
    }
    return el;
  };

  const showFieldError = (field, message) => {
    const hasMsg = Boolean(message);

    if (field === 'photo') {
      const err = ensureFieldErrorEl('photo');
      err.textContent = message || 'This field is required';
      err.hidden = !hasMsg;

      uploadBox?.classList?.toggle('ai-invalid', hasMsg);
      photoInput?.setAttribute?.('aria-invalid', hasMsg ? 'true' : 'false');
    }

    if (field === 'name') {
      const err = ensureFieldErrorEl('name');
      err.textContent = message || 'This field is required';
      err.hidden = !hasMsg;

      nameInput?.classList?.toggle('ai-invalid', hasMsg);
      nameInput?.setAttribute?.('aria-invalid', hasMsg ? 'true' : 'false');
    }
  };

  const validateBeforeGenerate = () => {
    let ok = true;

    if (!photoInput?.files?.[0]) {
      showFieldError('photo', 'This field is required');
      ok = false;
    } else {
      showFieldError('photo', '');
    }

    if (!nameInput?.value?.trim()) {
      showFieldError('name', 'This field is required');
      ok = false;
    } else {
      showFieldError('name', '');
    }

    return ok;
  };

  if (photoInput) on(photoInput, 'change', () => showFieldError('photo', ''));
  if (nameInput) on(nameInput, 'input', () => showFieldError('name', ''));

  /* ------------------------------ dog-only popup ------------------------------ */
  const showDogOnlyPopup = (ms = 2000) => {
    const el = document.getElementById('dog-only-popup');
    if (!el) return;

    if (dogPopupTimer) clearTimeout(dogPopupTimer);

    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');

    dogPopupTimer = setTimeout(() => {
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      clearStatus();
    }, ms);
  };

  /* ------------------------------ Email gate + Shopify Forms ------------------------------ */
  const isValidEmail = (value) => {
    const v = (value || '').trim();
    return /^(?!.*\.\.)[a-z0-9](?:[a-z0-9._%+-]{0,62}[a-z0-9])?@(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i.test(v);
  };

  const findShopifyFormsEmailInput = () => {
    const embed = document.querySelector('shopify-forms-embed#app-embed') || document.querySelector('shopify-forms-embed');
    if (!embed) return null;

    if (embed.shadowRoot) {
      const inp =
        embed.shadowRoot.querySelector('input[type="email"]') ||
        embed.shadowRoot.querySelector('input[name*="email" i]') ||
        embed.shadowRoot.querySelector('input[id*="email" i]');
      if (inp) return inp;
    }

    const iframe = embed.querySelector('iframe');
    if (iframe) {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return null;

        const inp =
          doc.querySelector('input[type="email"]') ||
          doc.querySelector('input[name*="email" i]') ||
          doc.querySelector('input[id*="email" i]');
        if (inp) return inp;
      } catch (_) {
        return null;
      }
    }

    return null;
  };

  let shopifyFormsEmailInput = null;

  const getEmailRoot = (emailInput) => {
    const rootNode = typeof emailInput?.getRootNode === 'function' ? emailInput.getRootNode() : null;
    if (rootNode && typeof rootNode.querySelector === 'function') return rootNode;
    return emailInput?.ownerDocument || document;
  };

  const ensureEmailErrorEl = (emailInput) => {
    if (!emailInput) return null;

    const rootNode = getEmailRoot(emailInput);
    let all = Array.from(rootNode.querySelectorAll('[data-ai-error="email"]'));

    if (all.length > 1) {
      all.slice(1).forEach((el) => el.remove());
      all = all.slice(0, 1);
    }

    let errEl = all[0] || null;
    if (!errEl) {
      errEl = (rootNode.createElement ? rootNode.createElement('small') : document.createElement('small'));
      errEl.dataset.aiError = 'email';
      errEl.className = 'ai-field-error';
      errEl.style.cssText = 'color:#d72c0d;font-size:16px;margin-top:8px;line-height:1.2;';
      errEl.hidden = true;

      const wrapper = emailInput.closest('div') || emailInput.parentElement;
      (wrapper || emailInput).appendChild(errEl);
    }
    return errEl;
  };

  const toggleFieldError = (el, errEl, show) => {
    if (errEl) errEl.hidden = !show;
    el?.setAttribute?.('aria-invalid', show ? 'true' : 'false');
  };

  const hideEmailBlock = () => {
    const wrap = document.querySelector('.ai-email-custom');
    if (!wrap) return;

    const embed =
      wrap.querySelector('shopify-forms-embed#app-embed') ||
      wrap.querySelector('shopify-forms-embed') ||
      wrap.querySelector('[id*="forms_inline" i]') ||
      wrap.querySelector('[class*="forms_inline" i]') ||
      wrap.firstElementChild;

    if (embed) embed.style.setProperty('display', 'none', 'important');
  };

  const showEmailBlock = () => {
    const wrap = document.querySelector('.ai-email-custom');
    if (!wrap) return;

    const embed =
      wrap.querySelector('shopify-forms-embed#app-embed') ||
      wrap.querySelector('shopify-forms-embed') ||
      wrap.querySelector('[id*="forms_inline" i]') ||
      wrap.querySelector('[class*="forms_inline" i]') ||
      wrap.firstElementChild;

    if (embed) embed.style.removeProperty('display');
  };

  const refreshEmailVisibility = () => {
    if (shouldRequireEmailGate()) {
      showEmailBlock();
    } else {
      hideEmailBlock();
    }
  };

  refreshEmailVisibility();

  const attachEmailInput = () => {
    shopifyFormsEmailInput = findShopifyFormsEmailInput();
    if (!shopifyFormsEmailInput) return false;
    if (shopifyFormsEmailInput.dataset.aiBound !== '1') {
      shopifyFormsEmailInput.dataset.aiBound = '1';
      on(shopifyFormsEmailInput, 'input', () => {});
    }
    return true;
  };

  void withRetries(() => attachEmailInput() || null, { interval: 300, timeout: 6000 });

  window.AI_NEWSLETTER = window.AI_NEWSLETTER || {
    submitBtn: null,

    findSubmitBtn() {
      const embed = document.querySelector('shopify-forms-embed#app-embed') || document.querySelector('shopify-forms-embed');
      if (!embed) return null;

      const findIn = (r) => r?.querySelector?.('button[type="submit"], input[type="submit"]') || null;

      if (embed.shadowRoot) {
        const btn = findIn(embed.shadowRoot);
        if (btn) return btn;
      }

      const iframe = embed.querySelector('iframe');
      if (iframe) {
        try {
          const doc = iframe.contentDocument || iframe.contentWindow?.document;
          const btn = findIn(doc);
          if (btn) return btn;
        } catch (_) {}
      }

      return findIn(embed);
    },

    click() {
      this.submitBtn = this.findSubmitBtn();
      if (!this.submitBtn) return false;
      this.submitBtn.click();
      return true;
    },

    hide() {
      const embed = document.querySelector('shopify-forms-embed#app-embed') || document.querySelector('shopify-forms-embed');
      if (!embed) return;

      embed.style.setProperty('display', 'none', 'important');

      const wrapper =
        embed.closest('[id*="__forms_inline" i]') ||
        embed.closest('[class*="forms_inline" i]') ||
        embed.closest('.shopify-app-block');

      if (wrapper) wrapper.style.setProperty('display', 'none', 'important');

      const box = document.querySelector('.ai-email-custom');
      if (box) box.style.setProperty('display', 'none', 'important');
    },
  };

  const tweakShopifyFormsAfterDelay = () => {
    const run = () => {
      const embed = document.querySelector('shopify-forms-embed#app-embed') || document.querySelector('shopify-forms-embed');
      if (!embed) return false;

      const apply = (rootNode) => {
        if (!rootNode) return false;

        const STYLE_ID = 'ai-newsletter-form-fixes';

        const getStyleHost = () => {
          if (rootNode instanceof ShadowRoot) return rootNode;
          const root = rootNode.getRootNode?.();
          if (root instanceof ShadowRoot) return root;
          return document.head;
        };

        const ensureStyles = () => {
          const styleHost = getStyleHost();
          if (!styleHost) return;

          const alreadyExists =
            styleHost.querySelector?.(`#${STYLE_ID}`) ||
            document.getElementById(STYLE_ID);

          if (alreadyExists) return;

          const style = document.createElement('style');
          style.id = STYLE_ID;


          // Fix 2: In the style block
          style.textContent = `
            .ai-newsletter-hide-submit {
              display: none !important;
            }
            .ai-newsletter-email-input {
              outline: none !important;
              box-shadow: none !important;
              border: 1px solid #C5C0BC !important;
              font-size: 18px !important;
              line-height: 1.2 !important;
              -webkit-text-size-adjust: 100% !important;
            }
            .ai-newsletter-fit-content {
              height: fit-content !important;
              min-height: unset !important;
              min-width: unset !important;  /* was "none" which is invalid */
            }
             ._inline_1q1d2_47 ._formContainer_1q1d2_30{
             width:100% !important;
             max-width:100% !important;
             } 

             ._container_3eyaa_70{
             display:block !important;
             }
          `;

          if (styleHost instanceof ShadowRoot) {
            styleHost.appendChild(style);
          } else {
            document.head.appendChild(style);
          }
        };

        ensureStyles();

        const btnSubmit = rootNode.querySelector?.('button[type="submit"], input[type="submit"]');
        if (btnSubmit) {
          window.AI_NEWSLETTER.submitBtn = btnSubmit;
          btnSubmit.classList.add('ai-newsletter-hide-submit');
        }

        const inp =
          rootNode.querySelector?.('input[type="email"]') ||
          rootNode.querySelector?.('input[name*="email" i]') ||
          rootNode.querySelector?.('input[id*="email" i]');

        if (inp) {
          inp.classList.add('ai-newsletter-email-input');
        }

        const dialogSection =
          rootNode.querySelector?.('section[data-sizing="form-wrapper"][role="dialog"]') ||
          rootNode.querySelector?.('section[data-sizing="form-wrapper"]');

        const wrapperSection =
        dialogSection?.closest?.('section[class*="_inline"]') ||
        rootNode.querySelector?.('section[class*="_inline"]');

        if (dialogSection) dialogSection.classList.add('ai-newsletter-fit-content');
        if (wrapperSection) wrapperSection.classList.add('ai-newsletter-fit-content');

        return true;
      };

      if (embed.shadowRoot) return apply(embed.shadowRoot);

      const iframe = embed.querySelector('iframe');
      if (iframe) {
        try {
          const doc = iframe.contentDocument || iframe.contentWindow?.document;
          if (doc) return apply(doc);
        } catch (_) {}
      }

      return apply(embed);
    };

    setTimeout(run, 300);
    setTimeout(run, 900);
    setTimeout(run, 1500);
    setTimeout(run, 2500);
  };

  tweakShopifyFormsAfterDelay();

  const validateEmailOrBlock = () => {
    if (!shouldRequireEmailGate()) return { ok: true, reason: 'already_has_images' };

    const emailInput = shopifyFormsEmailInput || findShopifyFormsEmailInput();
    if (!emailInput) return { ok: false, reason: 'email_input_not_found' };

    const email = (emailInput.value || '').trim();
    const errEl = ensureEmailErrorEl(emailInput);

    if (!email) {
      if (errEl) errEl.textContent = 'This field is required';
      toggleFieldError(emailInput, errEl, true);
      return { ok: false, reason: 'email_required' };
    }

    if (!isValidEmail(email)) {
      if (errEl) errEl.textContent = 'Please enter a valid email.';
      toggleFieldError(emailInput, errEl, true);
      return { ok: false, reason: 'invalid_email' };
    }

    toggleFieldError(emailInput, errEl, false);
    return { ok: true };
  };

  const gateEmailIfNoImagesStored = () => {
    if (!shouldRequireEmailGate()) return { ok: true, skipped: true };
    return validateEmailOrBlock();
  };

  /* ------------------------------ add-to-cart reset ------------------------------ */
  const clearLSState = () => {
    try { localStorage.removeItem(LS_KEY); } catch {}
  };

  let syncPropertiesToAllForms = () => {};
  let renderGridIfNeeded = () => {};
  let syncAiImagesToHorizonCarousel = () => {};

  const resetAiStateAfterAddToCart = () => {
    clearLSState();

    generatedImages.length = 0;
    selectedImage = null;
    generationCount = 0;

    _selectedImageFile = null;
    _selectedImageFileUrl = null;
    _selectedImageFilePromise = null;

    clearStatus();
    if (status) status.hidden = true;

    try { renderGridIfNeeded(); } catch (_) {}
    try { syncAiImagesToHorizonCarousel([], null); } catch (_) {}

    if (btn) btn.disabled = false;

    try { syncPropertiesToAllForms(); } catch (_) {}

    refreshEmailVisibility();
  };
  window.__AI_ACTIVE_RESET_AFTER_ATC = resetAiStateAfterAddToCart;

  const scheduleResetAfterSubmit = () => setTimeout(resetAiStateAfterAddToCart, 50);

  // ✅ Reset after successful AJAX /cart/add
  (function hookResetAfterAjaxAddToCartOnce() {
    if (window.__AI_RESET_AFTER_ATC_HOOKED) return;
    window.__AI_RESET_AFTER_ATC_HOOKED = true;

    let lastResetAt = 0;
    const debounceReset = () => {
      const now = Date.now();
      if (now - lastResetAt < 1200) return;
      lastResetAt = now;

      setTimeout(() => {
        try {
          const activeReset = window.__AI_ACTIVE_RESET_AFTER_ATC;
          if (typeof activeReset === 'function') activeReset();
        } catch (e) {
          console.warn('[AI] reset failed:', e);
        }
      }, 0);
    };

    const isCartAddUrl = (url) => !!url && (url.includes('/cart/add') || url.includes('/cart/add.js'));

    const bodyHasAiProps = (body) => {
      try {
        const keyFile = `properties[${FILE_PROPERTY_KEY}]`;
        const keyPet = 'properties[Pet name]';
        const keyStyle = 'properties[Style]';

        if (!body) return false;

        if (body instanceof FormData) return body.has(keyFile) || body.has(keyPet) || body.has(keyStyle);
        if (body instanceof URLSearchParams) return body.has(keyFile) || body.has(keyPet) || body.has(keyStyle);

        if (typeof body === 'string') {
          return (
            body.includes(keyFile) || body.includes(keyPet) || body.includes(keyStyle) ||
            body.includes(encodeURIComponent(keyFile)) ||
            body.includes(encodeURIComponent(keyPet)) ||
            body.includes(encodeURIComponent(keyStyle)) ||
            body.includes('properties%5BAI%20Image%5D') ||
            body.includes('properties%5BPet%20name%5D') ||
            body.includes('properties%5BStyle%5D')
          );
        }
      } catch (_) {}
      return false;
    };

    let lastAiAtcIntentAt = 0;
    document.addEventListener(
      'click',
      (e) => {
        const btn = e.target?.closest?.('button[name="add"], button[type="submit"], input[name="add"], input[type="submit"]');
        if (!btn) return;

        const form = btn.closest('form');
        if (!form) return;

        const hasOurProps =
          form.querySelector(`input[name="properties[Pet name]"]`) ||
          form.querySelector(`input[name="properties[Style]"]`) ||
          form.querySelector(`input[name="properties[${FILE_PROPERTY_KEY}]"]`);

        if (hasOurProps) lastAiAtcIntentAt = Date.now();
      },
      true
    );

    const shouldResetForRequest = (url, body) => {
      if (!isCartAddUrl(url)) return false;
      if (bodyHasAiProps(body)) return true;
      return Date.now() - lastAiAtcIntentAt < 5000;
    };

    if (typeof window.fetch === 'function' && !window.__AI_FETCH_PATCHED) {
      window.__AI_FETCH_PATCHED = true;
      const origFetch = window.fetch.bind(window);

      window.fetch = async (...args) => {
        const input = args[0];
        const init = args[1] || {};
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const body = init?.body;

        const relevant = shouldResetForRequest(url, body);
        const res = await origFetch(...args);

        if (relevant && res?.ok) debounceReset();
        return res;
      };
    }

    if (window.XMLHttpRequest && XMLHttpRequest.prototype && !window.__AI_XHR_PATCHED) {
      window.__AI_XHR_PATCHED = true;
      const XHROpen = XMLHttpRequest.prototype.open;
      const XHRSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function (method, url, ...rest) {
        this.__ai_url = url;
        return XHROpen.call(this, method, url, ...rest);
      };

      XMLHttpRequest.prototype.send = function (body) {
        try {
          const url = this.__ai_url || '';
          const relevant = shouldResetForRequest(url, body);

          if (relevant) {
            this.addEventListener(
              'loadend',
              () => {
                if (this.status >= 200 && this.status < 300) debounceReset();
              },
              { once: true }
            );
          }
        } catch (_) {}

        return XHRSend.call(this, body);
      };
    }
  })();

  /* ------------------------------ form sync (props + file) ------------------------------ */
  const getAllProductForms = () => Array.from(document.querySelectorAll('form[action^="/cart/add"]'));

  const styleLabel = (value) => {
    const map = {
      bold_portrait: 'Bold portrait',
      cubist: 'Cubist',
      floral: 'Floral',
      colorfull: 'Colorful',
    };
    return map[value] || value;
  };

  const setOrUpdateHiddenProperty = (form, key, value) => {
    if (!form) return;
    const inputName = `properties[${key}]`;
    let inp = form.querySelector(`input[name="${inputName}"]`);
    if (!inp) {
      inp = document.createElement('input');
      inp.type = 'hidden';
      inp.name = inputName;
      form.appendChild(inp);
    }
    inp.value = value || '';
  };

  const ensureFilePropertyInput = (form, key) => {
    if (!form) return null;
    const inputName = `properties[${key}]`;

    let inp = form.querySelector(`input[name="${inputName}"]`);
    if (!inp || inp.type !== 'file') {
      if (inp && inp.type !== 'file') inp.remove();

      inp = document.createElement('input');
      inp.type = 'file';
      inp.name = inputName;
      inp.accept = 'image/*';

      inp.style.position = 'absolute';
      inp.style.left = '-9999px';
      inp.style.width = '1px';
      inp.style.height = '1px';
      inp.style.opacity = '0';
      inp.tabIndex = -1;

      form.appendChild(inp);
    }
    return inp;
  };

  const setFileOnInput = (fileInput, file) => {
    if (!fileInput || !file) return false;
    try {
      const dt = new DataTransfer();
      dt.items.add(file);
      fileInput.files = dt.files;
      return true;
    } catch (_) {
      return false;
    }
  };

  const getExtFromMime = (mime) => {
    if (!mime) return 'png';
    if (mime.includes('jpeg') || mime.includes('jpg')) return 'jpg';
    if (mime.includes('png')) return 'png';
    if (mime.includes('webp')) return 'webp';
    return 'png';
  };

  const dataURLToBlob = (dataUrl) => {
    const [header, data] = dataUrl.split(',');
    const mime = (header.match(/data:(.*?);base64/) || [])[1] || 'image/png';
    const binary = atob(data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  };

  const fetchImageAsBlob = async (url) => {
    if (!url) throw new Error('Empty URL');

    if (url.startsWith('data:image/')) return dataURLToBlob(url);

    const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error('The image could not be downloaded.');
    return await res.blob();
  };

  const prepareSelectedImageFile = async () => {
    if (!selectedImage) return null;

    if (_selectedImageFile && _selectedImageFileUrl === selectedImage) return _selectedImageFile;
    if (_selectedImageFilePromise && _selectedImageFileUrl === selectedImage) return await _selectedImageFilePromise;

    _selectedImageFileUrl = selectedImage;

    _selectedImageFilePromise = (async () => {
      const blob = await fetchImageAsBlob(selectedImage);
      const ext = getExtFromMime(blob.type);
      const filename = `ai-image-${PRODUCT_ID}.${ext}`;
      const file = new File([blob], filename, { type: blob.type || 'image/png' });
      _selectedImageFile = file;
      return file;
    })();

    try {
      return await _selectedImageFilePromise;
    } finally {
      _selectedImageFilePromise = null;
    }
  };

  const syncFileToAllForms = (file) => {
    for (const form of getAllProductForms()) {
      const fileInput = ensureFilePropertyInput(form, FILE_PROPERTY_KEY);
      setFileOnInput(fileInput, file);
    }
  };

  syncPropertiesToAllForms = () => {
    for (const form of getAllProductForms()) {
      setOrUpdateHiddenProperty(form, 'Pet name', nameInput?.value || '');
      setOrUpdateHiddenProperty(form, 'Style', styleLabel(styleSelect?.value || ''));
      // setOrUpdateHiddenProperty(form, URL_PROPERTY_KEY, selectedImage || '');

      ensureFilePropertyInput(form, FILE_PROPERTY_KEY);
    }
  };

  const attachFormGuardToAll = () => {
    for (const form of getAllProductForms()) {
      if (form.dataset.aiBound === '1') continue;
      form.dataset.aiBound = '1';

      on(
        form,
        'submit',
        (e) => {
          if (form.__aiResubmitting) {
            form.__aiResubmitting = false;
            scheduleResetAfterSubmit();
            return;
          }

          syncPropertiesToAllForms();

          if (!selectedImage) {
            scheduleResetAfterSubmit();
            return;
          }

          const fileInput = ensureFilePropertyInput(form, FILE_PROPERTY_KEY);
          const alreadyHasFile = !!fileInput?.files?.length;
          if (alreadyHasFile) {
            scheduleResetAfterSubmit();
            return;
          }

          e.preventDefault();

          (async () => {
            try {
              const file = await prepareSelectedImageFile();
              if (file) syncFileToAllForms(file);
            } catch (err) {
              console.warn('[AI] Could not prepare file (CORS?). Sending only URL:', err);
            } finally {
              form.__aiResubmitting = true;

              const submitBtn = form.querySelector('[type="submit"]');
              if (typeof form.requestSubmit === 'function') {
                form.requestSubmit(submitBtn || undefined);
              } else {
                form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
              }
            }
          })();
        },
        { capture: true }
      );
    }
  };

  attachFormGuardToAll();

  if (nameInput) on(nameInput, 'input', () => { syncPropertiesToAllForms(); saveStateToLS(); });
  if (styleSelect) on(styleSelect, 'change', () => { syncPropertiesToAllForms(); saveStateToLS(); });

  /* ------------------------------ style pills ------------------------------ */
  const syncPillsFromSelect = () => {
    if (!pillButtons.length || !styleSelect) return;
    const current = styleSelect.value;

    for (const pill of pillButtons) {
      const v = pill.getAttribute('data-ai-style-pill');
      const active = v === current;
      pill.classList.toggle('is-active', active);
      pill.setAttribute('aria-selected', active ? 'true' : 'false');
    }
  };

  if (pillButtons.length && styleSelect) {
    for (const pill of pillButtons) {
      on(pill, 'click', () => {
        const v = pill.getAttribute('data-ai-style-pill');
        if (!v) return;
        styleSelect.value = v;
        styleSelect.dispatchEvent(new Event('change', { bubbles: true }));
        syncPillsFromSelect();
      });
    }
    syncPillsFromSelect();
    on(styleSelect, 'change', syncPillsFromSelect);
  }

  /* ====================================================================== */
  /* ========= Gallery (Horizon carousel) — FIXED VERSION ================ */
  /* ====================================================================== */

  const getHorizonGallery = () => {
    const mediaGallery =
      document.querySelector('media-gallery[data-presentation="carousel"]') ||
      document.querySelector('media-gallery');

    if (!mediaGallery) return null;

    const slideshow = mediaGallery.querySelector('slideshow-component');
    if (!slideshow) return null;

    const firstSlide = slideshow.querySelector('slideshow-slide');
    if (!firstSlide || !firstSlide.parentElement) return null;

    const rail = firstSlide.parentElement;

    const dotsWrap =
      mediaGallery.querySelector('ol.slideshow-controls__dots') ||
      slideshow.querySelector('ol.slideshow-controls__dots');

    return { mediaGallery, slideshow, rail, dotsWrap };
  };

  const hzGetSlides = (g) => Array.from(g.rail.querySelectorAll('slideshow-slide'));
  const hzGetDots = (g) => (g.dotsWrap ? Array.from(g.dotsWrap.querySelectorAll('button.slideshow-control')) : []);

  const hzCurrentAiUrls = (g) =>
    hzGetSlides(g)
      .filter((s) => s.getAttribute('data-ai') === '1')
      .map((s) => s.getAttribute('data-main-src') || '')
      .filter(Boolean);

  const hzRemoveAiItems = (g) => {
    hzGetSlides(g).forEach((slide) => {
      if (slide.getAttribute('data-ai') === '1') slide.remove();
    });
  };

  const hzSyncSlideMeta = (g) => {
    const slides = hzGetSlides(g);
    const slideshow = g.slideshow;
    if (!slideshow) return;

    const timelineList = slides.map((_, i) => `--slide-${i + 1}`).join(', ');

    const currentStyle = slideshow.getAttribute('style') || '';
    const cleanedStyle = currentStyle.replace(/--slideshow-timeline:\s*[^;]+;?/g, '').trim();
    slideshow.setAttribute(
      'style',
      `${cleanedStyle}${cleanedStyle && !cleanedStyle.endsWith(';') ? ';' : ''}--slideshow-timeline: ${timelineList};`
    );

    slides.forEach((slide, i) => {
      const slideStyle = slide.getAttribute('style') || '';
      const cleanedSlideStyle = slideStyle.replace(/--slideshow-timeline:\s*[^;]+;?/g, '').trim();
      slide.setAttribute(
        'style',
        `${cleanedSlideStyle}${cleanedSlideStyle && !cleanedSlideStyle.endsWith(';') ? ';' : ''} --slideshow-timeline: --slide-${i + 1};`
      );
    });
  };

  const hzUpdateCounter = (g, activeIdx = null) => {
    const total = hzGetSlides(g).length;
    const counters = Array.from(g.mediaGallery.querySelectorAll('.slideshow-controls__counter'));

    counters.forEach((counter) => {
      const currentEl = counter.querySelector('[ref="current"]') || counter.querySelector('span');
      if (currentEl && activeIdx !== null) currentEl.textContent = String(activeIdx + 1);

      const slash = counter.querySelector('.slash');
      if (!slash) return;

      if (slash.nextElementSibling) {
        slash.nextElementSibling.textContent = String(total);
        return;
      }

      const childNodes = Array.from(counter.childNodes);
      const slashIdx = childNodes.indexOf(slash);
      if (slashIdx >= 0) {
        for (let i = slashIdx + 1; i < childNodes.length; i++) {
          const node = childNodes[i];
          if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
            node.textContent = String(total);
            return;
          }
        }
        const textNode = document.createTextNode(String(total));
        slash.after(textNode);
        return;
      }

      const spans = counter.querySelectorAll('span');
      if (spans[2]) spans[2].textContent = String(total);
    });
  };

  const hzRebuildMobileDots = (g) => {
    if (!g.dotsWrap) return;

    const slides = hzGetSlides(g);
    const targetCount = slides.length;
    const existingLis = Array.from(g.dotsWrap.querySelectorAll(':scope > li'));
    const currentCount = existingLis.length;

    if (currentCount === targetCount) {
      existingLis.forEach((li, i) => {
        const btnCntrl = li.querySelector('button.slideshow-control');
        if (!btnCntrl) return;
        btnCntrl.setAttribute('aria-label', `Slide ${i + 1} of ${targetCount}`);
        btnCntrl.setAttribute('aria-selected', slides[i]?.classList.contains('is-active') ? 'true' : 'false');
        btnCntrl.style.animationTimeline = `--slide-${i + 1}`;
      });
      return;
    }

    while (g.dotsWrap.children.length > targetCount) {
      g.dotsWrap.lastElementChild?.remove();
    }

    while (g.dotsWrap.children.length < targetCount) {
      const i = g.dotsWrap.children.length;
      const li = document.createElement('li');
      const btnCntrl = document.createElement('button');

      btnCntrl.type = 'button';
      btnCntrl.className = 'slideshow-control button button-unstyled';
      btnCntrl.setAttribute('aria-label', `Slide ${i + 1} of ${targetCount}`);
      btnCntrl.setAttribute('aria-selected', 'false');
      btnCntrl.style.animationTimeline = `--slide-${i + 1}`;
      btnCntrl.textContent = String(i + 1);

      const slideIdx = i;
      btnCntrl.addEventListener('click', () => {
        hzMarkActive(g, slideIdx, true);
        hzUpdateCounter(g, slideIdx);
      });

      li.appendChild(btnCntrl);
      g.dotsWrap.appendChild(li);
    }

    Array.from(g.dotsWrap.querySelectorAll(':scope > li')).forEach((li, i) => {
      const btnCntrl = li.querySelector('button.slideshow-control');
      if (!btnCntrl) return;
      btnCntrl.setAttribute('aria-label', `Slide ${i + 1} of ${targetCount}`);
      btnCntrl.setAttribute('aria-selected', slides[i]?.classList.contains('is-active') ? 'true' : 'false');
      btnCntrl.style.animationTimeline = `--slide-${i + 1}`;
    });
  };

  const hzMarkActive = (g, idx, shouldScroll = true) => {
    const slides = hzGetSlides(g);
    if (!slides[idx]) return false;

    slides.forEach((slide, i) => {
      const active = i === idx;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.classList.toggle('is-active', active);
    });

    const dots = hzGetDots(g);
    dots.forEach((dot, i) => {
      dot.setAttribute('aria-selected', i === idx ? 'true' : 'false');
    });

    hzUpdateCounter(g, idx);

    try {
      g.slideshow.setAttribute('initial-slide', String(idx));
    } catch (_) {}

    if (shouldScroll) {
      const targetSlide = slides[idx];

      let usedNative = false;
      try {
        const comp = g.slideshow;
        if (typeof comp.goToSlide === 'function') {
          comp.goToSlide(idx);
          usedNative = true;
        } else if (typeof comp.select === 'function') {
          comp.select(idx);
          usedNative = true;
        } else if (typeof comp.scrollToSlide === 'function') {
          comp.scrollToSlide(idx);
          usedNative = true;
        }
      } catch (_) {}

      if (!usedNative) {
        try {
          const railRect = g.rail.getBoundingClientRect();
          const slideRect = targetSlide.getBoundingClientRect();
          const scrollOffset = slideRect.left - railRect.left + g.rail.scrollLeft;
          g.rail.scrollTo({ left: scrollOffset });
        } catch (_) {
          try {
            g.rail.scrollLeft = targetSlide.offsetLeft;
          } catch (_) {}
        }
      }
    }

    return true;
  };

  const hzCreateAiSlideFromBase = (baseSlide, src, index = 0) => {
    const clone = baseSlide.cloneNode(true);
    const uniqueId = `ai-slide-${Date.now()}-${index}`;

    clone.setAttribute('data-ai', '1');
    clone.setAttribute('data-main-src', src);
    clone.setAttribute('aria-hidden', 'true');
    clone.classList.remove('is-active');

    clone.setAttribute('ref', 'slides[]');

    clone.removeAttribute('id');
    clone.removeAttribute('on:click');
    clone.removeAttribute('data-view-transition-type');

    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));

    clone.querySelectorAll('deferred-media').forEach((el) => el.remove());

    const media = clone.querySelector('.product-media');
    if (media) {
      media.setAttribute('data-media-id', uniqueId);
    }

    const img = clone.querySelector('img.product-media__image') || clone.querySelector('img');
    if (img) {
      img.src = src;
      img.setAttribute('src', src);
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.setAttribute('data_max_resolution', src);
      img.setAttribute('loading', 'lazy');
      img.setAttribute('fetchpriority', 'auto');
      img.setAttribute('transitiontoproduct', 'false');
      img.classList.add('ai-generated');
    }

    return clone;
  };

  const hzSyncAiToZoomDialog = (g, urls) => {
    const zoomDialog =
      g.mediaGallery.parentElement?.querySelector('zoom-dialog') ||
      g.mediaGallery.closest('[data-section-id]')?.querySelector('zoom-dialog') ||
      document.querySelector('zoom-dialog');
    if (!zoomDialog) return;

    const galleryList =
      zoomDialog.querySelector('ul.dialog-zoomed-gallery') ||
      zoomDialog.querySelector('.dialog-zoomed-gallery');
    if (!galleryList) return;

    const thumbnailsContainer =
      zoomDialog.querySelector('scroll-hint.dialog-thumbnails-list') ||
      zoomDialog.querySelector('.dialog-thumbnails-list');

    galleryList.querySelectorAll('[data-ai="1"]').forEach((el) => el.remove());
    if (thumbnailsContainer) {
      thumbnailsContainer.querySelectorAll('[data-ai="1"]').forEach((el) => el.remove());
    }

    if (!urls.length) return;

    const baseLi = galleryList.querySelector('li:not([data-ai])');
    const baseThumb = thumbnailsContainer?.querySelector('button:not([data-ai])');

    urls.forEach((src, i) => {
      let li;
      if (baseLi) {
        li = baseLi.cloneNode(true);
        li.setAttribute('data-ai', '1');
        li.removeAttribute('id');
        li.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));

        li.querySelectorAll('deferred-media, video, model-viewer').forEach((el) => el.remove());

        const img = li.querySelector('img');
        if (img) {
          img.src = src;
          img.setAttribute('src', src);
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.setAttribute('loading', 'lazy');
          img.classList.add('ai-generated');
          img.alt = `AI Generated Image ${i + 1}`;
        }

        const mediaDiv = li.querySelector('.product-media, [data-media-id]');
        if (mediaDiv) {
          mediaDiv.setAttribute('data-media-id', `ai-zoom-${Date.now()}-${i}`);
        }
      } else {
        li = document.createElement('li');
        li.setAttribute('data-ai', '1');
        li.className = 'product-media-container constrain-height product-media-container--image';
        li.innerHTML = `
          <div class="product-media" style="--ratio: 0.6666666666666666" data-media-id="ai-zoom-${Date.now()}-${i}">
            <img src="${src}" alt="AI Generated Image ${i + 1}"
                 loading="lazy" class="product-media__image ai-generated"
                 style="--focal-point: 50.0% 50.0%;">
          </div>
        `;
      }
      galleryList.appendChild(li);

      if (thumbnailsContainer) {
        let thumbBtn;
        if (baseThumb) {
          thumbBtn = baseThumb.cloneNode(true);
          thumbBtn.setAttribute('data-ai', '1');
          thumbBtn.removeAttribute('id');
          thumbBtn.setAttribute('aria-label', `AI Generated Image ${i + 1}`);

          const thumbImg = thumbBtn.querySelector('img');
          if (thumbImg) {
            thumbImg.src = src;
            thumbImg.setAttribute('src', src);
            thumbImg.removeAttribute('srcset');
            thumbImg.alt = `AI Generated ${i + 1}`;
            thumbImg.classList.add('ai-generated');
          }
        } else {
          thumbBtn = document.createElement('button');
          thumbBtn.type = 'button';
          thumbBtn.setAttribute('data-ai', '1');
          thumbBtn.className = 'button button-unstyled dialog-thumbnails-list__thumbnail';
          thumbBtn.setAttribute('aria-label', `AI Generated Image ${i + 1}`);
          thumbBtn.style.cssText = '--aspect-ratio: 1/1.25; --gallery-aspect-ratio: 1/1.25;';
          thumbBtn.innerHTML = `<img src="${src}" alt="AI Generated ${i + 1}" loading="lazy"
                                     style="object-position: 50.0% 50.0%;">`;
        }
        thumbnailsContainer.appendChild(thumbBtn);
      }
    });
  };

  let _aiSyncing = false;

  syncAiImagesToHorizonCarousel = (urls, activeUrl, shouldScroll = false) => {
    if (_aiSyncing) return false;
    _aiSyncing = true;

    try {
      return _doSyncAiImages(urls, activeUrl, shouldScroll);
    } finally {
      queueMicrotask(() => { _aiSyncing = false; });
    }
  };

  const _doSyncAiImages = (urls, activeUrl, shouldScroll) => {
    const g = getHorizonGallery();
    if (!g) return false;

    const cleanUrls = [...new Set((Array.isArray(urls) ? urls : []).filter(Boolean))];
    const existing = hzCurrentAiUrls(g);

    if (
      existing.length === cleanUrls.length &&
      existing.every((url, i) => url === cleanUrls[i])
    ) {
      hzSyncSlideMeta(g);
      hzRebuildMobileDots(g);

      if (activeUrl) {
        const idx = hzGetSlides(g).findIndex(
          (slide) => (slide.getAttribute('data-main-src') || '') === activeUrl
        );
        if (idx >= 0) {
          hzMarkActive(g, idx, shouldScroll);
          return true;
        }
      }

      const activeSlide = hzGetSlides(g).findIndex((s) => s.classList.contains('is-active'));
      hzUpdateCounter(g, activeSlide >= 0 ? activeSlide : 0);
      return true;
    }

    hzRemoveAiItems(g);

    if (!cleanUrls.length) {
      hzSyncSlideMeta(g);
      hzRebuildMobileDots(g);
      hzUpdateCounter(g, 0);
      hzSyncAiToZoomDialog(g, []);
      return true;
    }

    const baseSlides = hzGetSlides(g).filter((slide) => slide.getAttribute('data-ai') !== '1');
    const baseSlide = baseSlides[0];
    if (!baseSlide) return false;

    cleanUrls.forEach((src, i) => {
      const aiSlide = hzCreateAiSlideFromBase(baseSlide, src, i);
      g.rail.appendChild(aiSlide);
    });

    hzSyncAiToZoomDialog(g, cleanUrls);

    _aiSyncing = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        _aiSyncing = true;
        try {
          hzSyncSlideMeta(g);
          hzRebuildMobileDots(g);

          const slides = hzGetSlides(g);

          if (activeUrl) {
            const idx = slides.findIndex(
              (slide) => (slide.getAttribute('data-main-src') || '') === activeUrl
            );

            if (idx >= 0) {
              hzMarkActive(g, idx, shouldScroll);
              return;
            }
          }

          const firstAiIdx = slides.findIndex((slide) => slide.getAttribute('data-ai') === '1');
          if (firstAiIdx >= 0) {
            hzMarkActive(g, firstAiIdx, shouldScroll);
            return;
          }

          hzUpdateCounter(g, 0);
        } finally {
          queueMicrotask(() => { _aiSyncing = false; });
        }
      });
    });

    return true;
  };

  (() => {
    const zoomDialog = document.querySelector('zoom-dialog');
    if (!zoomDialog) return;

    const dialogEl = zoomDialog.querySelector('dialog') || zoomDialog;
    const zoomObserver = new MutationObserver(() => {
      const isOpen = dialogEl.hasAttribute('open') || dialogEl.open;
      if (isOpen && generatedImages.length) {
        const g = getHorizonGallery();
        if (g) {
          setTimeout(() => {
            hzSyncAiToZoomDialog(g, generatedImages.map((x) => x.url));
          }, 50);
        }
      }
    });
    zoomObserver.observe(dialogEl, { attributes: true, attributeFilter: ['open'] });
    signal.addEventListener('abort', () => zoomObserver.disconnect(), { once: true });
  })();

  /* ------------------------------ Grid render ------------------------------ */
  const getGridRenderKey = () =>
    JSON.stringify({
      selectedImage: selectedImage || null,
      generatedImages: generatedImages.map((item) => item?.url || null).filter(Boolean),
    });

  renderGridIfNeeded = () => {
    if (!results || !grid) return;

    if (generatedImages.length < 1) {
      if (chooseLabel) chooseLabel.hidden = true;
      results.hidden = true;
      grid.hidden = true;
      grid.removeAttribute('data-ai-render-key');
      grid.innerHTML = '';
      return;
    }

    results.hidden = false;
    if (chooseLabel) chooseLabel.hidden = false;
    grid.hidden = false;

    const renderKey = getGridRenderKey();
    if (
      grid.getAttribute('data-ai-render-key') === renderKey &&
      grid.children.length === generatedImages.length
    ) {
      return;
    }

    grid.setAttribute('data-ai-render-key', renderKey);
    grid.innerHTML = '';

    generatedImages.forEach((item, idx) => {
      const wrap = document.createElement('button');
      wrap.type = 'button';
      wrap.className = 'ai-option';
      wrap.setAttribute('aria-label', `Generated option ${idx + 1}`);

      if (item.url === selectedImage) wrap.classList.add('selected');

      const img = document.createElement('img');
      img.src = item.url;
      img.alt = `Option ${idx + 1}`;
      img.loading = 'lazy';

      wrap.appendChild(img);

      wrap.addEventListener('click', async () => {
        selectedImage = item.url;

        _selectedImageFile = null;
        _selectedImageFileUrl = null;

        syncPropertiesToAllForms();

        syncAiImagesToHorizonCarousel(
          generatedImages.map((x) => x.url),
          selectedImage,
          true
        );

        try {
          const file = await prepareSelectedImageFile();
          if (file) syncFileToAllForms(file);
        } catch (_) {}

        saveStateToLS();
        renderGridIfNeeded();
        scheduleRefresh();
      });

      grid.appendChild(wrap);
    });
  };

  /* ------------------------------ Mobile modal (preview) ------------------------------ */
  const isMobile = () => window.matchMedia && window.matchMedia('(max-width: 767px)').matches;

  const getMobileModal = () => {
    const rootEl = document.getElementById('ai-mobile-preview-modal');
    if (!rootEl) return null;

    const imgEl = rootEl.querySelector('[data-ai-modal-img]');
    const closeBtn = rootEl.querySelector('[data-ai-modal-close]');
    const backdrop = rootEl.querySelector('[data-ai-modal-backdrop]');
    if (!imgEl || !closeBtn || !backdrop) return null;

    return { rootEl, imgEl, closeBtn, backdrop };
  };

  const scrollToProductInfo = () => {
    const target = document.getElementById('product-information-home');
    if (!target) return;
    const y = target.getBoundingClientRect().top + window.pageYOffset - 12;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const closeMobilePreview = () => {
    const m = getMobileModal();
    if (!m) return;

    m.rootEl.hidden = true;
    m.rootEl.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('ai-modal-open');

    scrollToProductInfo();
  };

  const openMobilePreview = (url) => {
    if (!url || !isMobile()) return;

    const m = getMobileModal();
    if (!m) return;

    m.imgEl.src = url;
    m.rootEl.hidden = false;
    m.rootEl.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('ai-modal-open');
  };

  (() => {
    const m = getMobileModal();
    if (!m) return;
    if (m.rootEl.dataset.bound === '1') return;
    m.rootEl.dataset.bound = '1';

    m.closeBtn.addEventListener('click', closeMobilePreview);
    m.backdrop.addEventListener('click', closeMobilePreview);
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && !m.rootEl.hidden) closeMobilePreview();
      },
      true
    );
  })();

  /* ------------------------------ hydrate from LS ------------------------------ */
  (function hydrateFromLS() {
    const saved = readLS();
    if (!saved) return;

    if (nameInput && typeof saved.petName === 'string') nameInput.value = saved.petName || '';
    if (styleSelect && typeof saved.style === 'string' && saved.style) {
      styleSelect.value = saved.style;
      styleSelect.dispatchEvent(new Event('change', { bubbles: true }));
      syncPillsFromSelect();
    }

    const imgs = Array.isArray(saved.generatedImages) ? saved.generatedImages : [];
    imgs.slice(0, MAX_GENERATIONS).forEach((it) => {
      if (it?.url) generatedImages.push({ url: it.url, createdAt: it.createdAt || Date.now() });
    });

    generationCount = typeof saved.generationCount === 'number' ? saved.generationCount : generatedImages.length;

    if (saved.selectedImage && generatedImages.some((x) => x.url === saved.selectedImage)) {
      selectedImage = saved.selectedImage;
    } else {
      selectedImage = generatedImages[0]?.url || null;
    }

    refreshEmailVisibility();

  syncAiImagesToHorizonCarousel(generatedImages.map((x) => x.url), selectedImage, true);

    if (selectedImage) {
      const retryScrollToAiSlide = () => {
        const g = getHorizonGallery();
        if (!g) return;
        const slides = hzGetSlides(g);
        const idx = slides.findIndex(
          (slide) => (slide.getAttribute('data-main-src') || '') === selectedImage
        );
        if (idx >= 0) hzMarkActive(g, idx, true);
      };
      setTimeout(retryScrollToAiSlide, 500);
      setTimeout(retryScrollToAiSlide, 1200);
    }

    syncPropertiesToAllForms();
    renderGridIfNeeded();
    attachFormGuardToAll();
    scheduleRefresh();

    if (generationCount >= MAX_GENERATIONS || generatedImages.length >= MAX_GENERATIONS) {
      generationCount = MAX_GENERATIONS;
      showLimitMessage();
      disableGenerateDueToLimit();
    }

    if (selectedImage) {
      prepareSelectedImageFile()
        .then((file) => file && syncFileToAllForms(file))
        .catch(() => {});
    }

    saveStateToLS();
  })();

  /* ------------------------------ keep images on variant switch / re-render ------------------------------ */
  const resyncAfterRerender = rafThrottle(() => {
    if (_aiSyncing) return;

    if (!generatedImages.length) {
      refreshEmailVisibility();
      return;
    }

    attachEmailInput();
    tweakShopifyFormsAfterDelay();

    syncAiImagesToHorizonCarousel(generatedImages.map((x) => x.url), selectedImage, false);

    attachFormGuardToAll();
    syncPropertiesToAllForms();

    renderGridIfNeeded();

    scheduleRefresh();
  });

  const scheduleResyncAfterVariantUpdate = () => {
    setTimeout(() => {
      if (signal.aborted) return;
      resyncAfterRerender();
    }, 0);
  };
  on(section.closest('.shopify-section') || document, 'variant:update', scheduleResyncAfterVariantUpdate);

  const isWithinGeneratorUi = (node) => {
    if (!(node instanceof Node)) return false;

    const modalRoot = getMobileModal()?.rootEl || null;
    return [section, loadingBox, modalRoot].some(
      (rootEl) => rootEl instanceof Node && (node === rootEl || rootEl.contains(node))
    );
  };

  const getMutationElement = (node, fallback) => {
    if (node instanceof Element) return node;
    if (node?.parentElement instanceof Element) return node.parentElement;
    if (fallback instanceof Element) return fallback;
    if (fallback?.parentElement instanceof Element) return fallback.parentElement;
    return null;
  };

  const isRelevantProductMutation = (mutation) => {
    const touchedElements = [mutation.target, ...Array.from(mutation.addedNodes), ...Array.from(mutation.removedNodes)]
      .map((node) => getMutationElement(node, mutation.target))
      .filter(Boolean);

    if (!touchedElements.length) return false;

    return touchedElements.some((el) => {
      if (isWithinGeneratorUi(el)) return false;
      return !!el.closest?.('product-form-component, media-gallery, zoom-dialog, .ai-email-custom, shopify-forms-embed');
    });
  };

  const watchNode = (node) => {
    if (!node) return null;
    const mo = new MutationObserver((mutations) => {
      if (!mutations.some(isRelevantProductMutation)) return;
      resyncAfterRerender();
    });
    mo.observe(node, { childList: true, subtree: true });
    signal.addEventListener('abort', () => mo.disconnect(), { once: true });
    return mo;
  };

  watchNode(document.querySelector('product-info') || scopeNode);

  /* ------------------------------ generate click ------------------------------ */
  if (btn) {
    on(btn, 'click', async () => {
      if (generationCount >= MAX_GENERATIONS || generatedImages.length >= MAX_GENERATIONS) {
        showLimitMessage();
        disableGenerateDueToLimit();
        return;
      }

      const gate = gateEmailIfNoImagesStored();
      const fieldsOk = validateBeforeGenerate();
      if (!gate.ok || !fieldsOk) return;

      try { window.AI_NEWSLETTER.click(); } catch (_) {}
      try { window.AI_NEWSLETTER.hide(); } catch (_) {}
      setTimeout(() => { try { window.AI_NEWSLETTER.hide(); } catch (_) {} }, 500);
      setTimeout(() => { try { window.AI_NEWSLETTER.hide(); } catch (_) {} }, 1500);

      refreshEmailVisibility();

      btn.disabled = true;
      if (chooseLabel) chooseLabel.hidden = true;
      clearStatus();
      showLoading();

      selectedImage = null;
      _selectedImageFile = null;
      _selectedImageFileUrl = null;

      syncPropertiesToAllForms();
      saveStateToLS();

      const fd = new FormData();
      fd.append('photo', photoInput?.files?.[0]);
      fd.append('petName', nameInput?.value || '');
      fd.append('style', styleSelect?.value || '');
      fd.append('productId', getProductId());
      fd.append('productHandle', getProductHandle());

      try {
        const res = await fetch('/apps/gemini/generate', { method: 'POST', body: fd });

        const raw = await res.text();
        let data = null;
        try { data = JSON.parse(raw); } catch {}

        if (res.status === 422) showDogOnlyPopup(4000);

        if (!res.ok || !data?.ok) {
          const msg = data?.error || 'Generation failed';
          throw new Error(msg);
        }

        const img = data.images?.[0];
        if (!img?.url) throw new Error('No image returned');

        generatedImages.push({ url: img.url, createdAt: Date.now() });
        if (generatedImages.length > MAX_GENERATIONS) {
          generatedImages.splice(0, generatedImages.length - MAX_GENERATIONS);
        }

        selectedImage = img.url;

        openMobilePreview(selectedImage);
        setTimeout(closeMobilePreview, 5000);

        syncPropertiesToAllForms();
        syncAiImagesToHorizonCarousel(generatedImages.map((x) => x.url), selectedImage, true);

        try {
          const file = await prepareSelectedImageFile();
          if (file) syncFileToAllForms(file);
        } catch (err) {
          console.warn('[AI] Could not prepare file (CORS). Only URL will be sent:', err);
        }

        generationCount = Math.min(MAX_GENERATIONS, generatedImages.length);
        saveStateToLS();

        hideLoading();
        renderGridIfNeeded();
        attachFormGuardToAll();
        scheduleRefresh();

        if (generationCount >= MAX_GENERATIONS || generatedImages.length >= MAX_GENERATIONS) {
          showLimitMessage();
          disableGenerateDueToLimit();
        }

        refreshEmailVisibility();
      } catch (err) {
        hideLoading();
        showError(err?.message || 'Error');
      } finally {
        if (generationCount < MAX_GENERATIONS && generatedImages.length < MAX_GENERATIONS) {
          btn.disabled = false;
        }
      }
    });
  }
};

/* Boot */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => window.AI_PET_GENERATOR.init(document));
} else {
  window.AI_PET_GENERATOR.init(document);
}
